"use strict";
import { ChartHeader } from "components/Charts/ChartHeader";
import { Chart, ChartModel } from "components/Charts/ChartModel";
import {
  RoundedCandleSeries
} from "components/Charts/PriceChart/RoundedCandlestickSeries/rounded-candles-series";
import { getCandlestickPriceBounds } from "components/Charts/PriceChart/utils";
import { PriceChartType } from "components/Charts/utils";
import { DeltaArrow, DeltaText, calculateDelta } from "components/Tokens/TokenDetails/Delta";
import {
  LineStyle,
  LineType
} from "lightweight-charts";
import { useMemo } from "react";
import { Trans } from "react-i18next";
import { Flex, Text, styled } from "ui/src";
import { opacify } from "ui/src/theme";
import { useLocalizationContext } from "uniswap/src/features/language/LocalizationContext";
import { NumberType } from "utilities/src/format/types";
const LOW_PRICE_RANGE_THRESHOLD = 0.2;
const LOW_PRICE_RANGE_SCALE_FACTOR = 1e9;
export class PriceChartModel extends ChartModel {
  series;
  originalData;
  lowPriceRangeScaleFactor = 1;
  type;
  minPriceLine;
  maxPriceLine;
  priceLineOptions;
  min;
  max;
  constructor(chartDiv, params) {
    super(chartDiv, params);
    this.originalData = this.data;
    const { adjustedData, lowPriceRangeScaleFactor, min, max } = PriceChartModel.getAdjustedPrices(params.data);
    this.data = adjustedData;
    this.lowPriceRangeScaleFactor = lowPriceRangeScaleFactor;
    this.min = min;
    this.max = max;
    this.type = params.type;
    this.series = this.type === PriceChartType.LINE ? this.api.addAreaSeries() : this.api.addCustomSeries(new RoundedCandleSeries());
    this.series.setData(this.data);
    this.updateOptions(params);
    this.fitContent();
  }
  static applyPriceScaleFactor(data, scaleFactor) {
    return {
      time: data.time,
      value: (data.value || data.close) * scaleFactor,
      open: data.open * scaleFactor,
      close: data.close * scaleFactor,
      high: data.high * scaleFactor,
      low: data.low * scaleFactor
    };
  }
  static getAdjustedPrices(data) {
    let lowPriceRangeScaleFactor = 1;
    let adjustedData = data;
    let { min, max } = getCandlestickPriceBounds(data);
    if (max - min < LOW_PRICE_RANGE_THRESHOLD) {
      lowPriceRangeScaleFactor = LOW_PRICE_RANGE_SCALE_FACTOR;
      adjustedData = data.map((point) => this.applyPriceScaleFactor(point, lowPriceRangeScaleFactor));
      min = min * lowPriceRangeScaleFactor;
      max = max * lowPriceRangeScaleFactor;
    }
    return { adjustedData, lowPriceRangeScaleFactor, min, max };
  }
  updateOptions(params) {
    const { data, theme, type, locale, format } = params;
    super.updateOptions(params, {
      localization: {
        locale,
        priceFormatter: (price) => {
          return format.convertFiatAmountFormatted(
            // Transform price back to original value if it was scaled
            Number(price) / this.lowPriceRangeScaleFactor,
            NumberType.FiatTokenPrice
          );
        }
      },
      grid: {
        vertLines: { style: LineStyle.CustomDotGrid, color: theme.neutral3 },
        horzLines: { style: LineStyle.CustomDotGrid, color: theme.neutral3 }
      }
    });
    if (this.type !== type) {
      this.type = params.type;
      this.api.removeSeries(this.series);
      if (this.type === PriceChartType.CANDLESTICK) {
        this.series = this.api.addCustomSeries(new RoundedCandleSeries());
      } else {
        this.series = this.api.addAreaSeries();
      }
      this.series.setData(this.data);
    }
    if (this.originalData !== data) {
      this.originalData = data;
      const { adjustedData, lowPriceRangeScaleFactor, min, max } = PriceChartModel.getAdjustedPrices(data);
      this.data = adjustedData;
      this.lowPriceRangeScaleFactor = lowPriceRangeScaleFactor;
      this.min = min;
      this.max = max;
      this.series.setData(this.data);
      this.fitContent();
    }
    const lineColor = theme.accent1;
    this.series.applyOptions({
      priceLineVisible: false,
      lastValueVisible: false,
      // Line-specific options:
      lineType: data.length < 20 ? LineType.WithSteps : LineType.Curved,
      // Stepped line is visually preferred for smaller datasets
      lineWidth: 2,
      lineColor,
      topColor: opacify(12, lineColor),
      bottomColor: opacify(12, lineColor),
      crosshairMarkerRadius: 5,
      crosshairMarkerBorderColor: opacify(30, lineColor),
      crosshairMarkerBorderWidth: 3,
      // Candlestick-specific options:
      upColor: theme.success,
      wickUpColor: theme.success,
      downColor: theme.critical,
      wickDownColor: theme.critical,
      borderVisible: false
    });
    this.priceLineOptions = {
      color: theme.surface3,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      axisLabelColor: theme.surface3Solid,
      axisLabelTextColor: theme.neutral1
    };
    this.minPriceLine?.applyOptions({ price: this.min, ...this.priceLineOptions });
    this.maxPriceLine?.applyOptions({ price: this.max, ...this.priceLineOptions });
  }
  onSeriesHover(hoverData) {
    if (hoverData) {
      const originalItem = this.originalData[hoverData.logicalIndex];
      const updatedHoverData = { ...hoverData, item: originalItem };
      super.onSeriesHover(updatedHoverData);
    } else {
      super.onSeriesHover(void 0);
    }
    if (hoverData === void 0) {
      if (this.minPriceLine && this.maxPriceLine) {
        this.series.removePriceLine(this.minPriceLine);
        this.series.removePriceLine(this.maxPriceLine);
        this.minPriceLine = void 0;
        this.maxPriceLine = void 0;
      }
    } else if (!this.minPriceLine && !this.maxPriceLine && this.min && this.max) {
      this.minPriceLine = this.series.createPriceLine({ price: this.min, ...this.priceLineOptions });
      this.maxPriceLine = this.series.createPriceLine({ price: this.max, ...this.priceLineOptions });
    }
  }
}
export function PriceChartDelta({ startingPrice, endingPrice, noColor }) {
  const delta = calculateDelta(startingPrice.close, endingPrice.close);
  const { formatPercent } = useLocalizationContext();
  return <Text variant="body2" display="flex" alignItems="center" gap="$gap4">{delta && <DeltaArrow delta={delta} formattedDelta={formatPercent(Math.abs(delta))} noColor={noColor} />}<DeltaText delta={delta}>{delta ? formatPercent(Math.abs(delta)) : "-"}</DeltaText></Text>;
}
const CandlestickTooltipRow = styled(Flex, {
  row: true,
  justifyContent: "space-between",
  gap: "$sm"
});
function CandlestickTooltip({ data }) {
  const { convertFiatAmountFormatted } = useLocalizationContext();
  return <><Text variant="body3" color="$neutral1"><CandlestickTooltipRow><Trans i18nKey="chart.price.label.open" /><Flex>{convertFiatAmountFormatted(data.open, NumberType.FiatTokenPrice)}</Flex></CandlestickTooltipRow><CandlestickTooltipRow><Trans i18nKey="chart.price.label.high" /><Flex>{convertFiatAmountFormatted(data.high, NumberType.FiatTokenPrice)}</Flex></CandlestickTooltipRow><CandlestickTooltipRow><Trans i18nKey="chart.price.label.low" /><Flex>{convertFiatAmountFormatted(data.low, NumberType.FiatTokenPrice)}</Flex></CandlestickTooltipRow><CandlestickTooltipRow><Trans i18nKey="chart.price.label.close" /><Flex>{convertFiatAmountFormatted(data.close, NumberType.FiatTokenPrice)}</Flex></CandlestickTooltipRow></Text></>;
}
export function PriceChart({ data, height, type, stale, symbol, fdv, vol }) {
  const lastPrice = data[data.length - 1];
  return <Chart
    Model={PriceChartModel}
    params={useMemo(() => ({ data, type, stale }), [data, stale, type])}
    height={height}
    TooltipBody={type === PriceChartType.CANDLESTICK ? CandlestickTooltip : void 0}
  >{(crosshairData) => <ChartHeader
    symbol={symbol}
    fdv={fdv}
    vol={vol}
    value={(crosshairData ?? lastPrice).value}
    additionalFields={<PriceChartDelta startingPrice={data[0]} endingPrice={crosshairData ?? lastPrice} />}
    valueFormatterType={NumberType.FiatTokenPrice}
    time={crosshairData?.time}
  />}</Chart>;
}
